import React, { useState } from 'react';
import {
  MonoTag,
  AmInput,
  DoubleRule,
  ExchangeStatusChip,
  ExchangeInstructions,
} from '../shared';
import { EXCHANGES, HIDDEN_CONNECTION_EXCHANGES } from '../data';
import { Exchange } from '../../../../types/exchange';

export type ExchangeStatus = 'pending' | 'testing' | 'ok' | 'fail';

export interface ExchangeValues {
  selected: Exchange;
  key: string;
  secret: string;
  passphrase: string;
  status: ExchangeStatus;
}

const ExchangeStep: React.FC<{
  values: ExchangeValues;
  onSelect: (e: Exchange) => void;
  onField: (key: 'key' | 'secret' | 'passphrase', value: string) => void;
  onTest: () => void;
  error?: string;
  phone?: boolean;
  narrow?: boolean;
}> = ({ values, onSelect, onField, onTest, error, phone, narrow }) => {
  const visibleExchanges = EXCHANGES.filter(
    (e) => !HIDDEN_CONNECTION_EXCHANGES.includes(e.id),
  );
  const ex =
    visibleExchanges.find((e) => e.id === values.selected) ||
    visibleExchanges[0];
  const accent = '#f08416';
  const connected = values.status === 'ok';
  // On mobile the instructions collapse (default closed) to keep the step short.
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const canTest =
    !!values.key && !!values.secret && values.status !== 'testing';
  return (
    <div
      style={{
        padding: phone ? '28px 20px 20px' : '48px 56px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        minHeight: 0,
      }}
    >
      <div>
        <h1
          style={{
            margin: '0 0 12px 0',
            fontSize: phone ? 34 : 56,
            fontWeight: 600,
            lineHeight: 0.95,
            letterSpacing: '-.01em',
          }}
        >
          Plug into the <span style={{ color: '#f08416' }}>exchange.</span>
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            color: '#9a9a9a',
            maxWidth: 780,
            lineHeight: 1.6,
          }}
        >
          Bots need limited access to read balances and execute spot trades.
          Withdraw permission is never requested. Selected exchange determines
          which bots appear in Discover. You can add multiple exchange
          connections after the onboarding is complete.
        </p>
      </div>
      <DoubleRule />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
          gap: narrow ? 28 : 64,
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className="no-scrollbar"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            minHeight: narrow ? undefined : 0,
            overflowY: narrow ? 'visible' : 'auto',
            scrollbarWidth: 'none',
            paddingRight: narrow ? 0 : 8,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: '#9a9a9a',
                textTransform: 'uppercase',
                letterSpacing: '.18em',
                marginBottom: 14,
                fontWeight: 500,
              }}
            >
              Exchange
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {visibleExchanges.map((e) => {
                const active = e.id === values.selected;
                const tileAccent = active && connected ? '#00B061' : accent;
                return (
                  <button
                    key={e.id}
                    onClick={() => onSelect(e.id)}
                    style={{
                      height: 56,
                      padding: '0 14px',
                      background: active ? '#0e0e0e' : '#000',
                      border: active
                        ? `1px solid ${tileAccent}`
                        : '1px solid #383838',
                      color: active ? tileAccent : '#9a9a9a',
                      fontFamily: 'Kanit',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      textAlign: 'left',
                      transition: 'border-color .12s,color .12s',
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        background: active ? tileAccent : 'transparent',
                        border: active ? 'none' : `1px solid ${tileAccent}`,
                        color: active ? '#000' : tileAccent,
                        fontFamily: 'Bebas Neue, Kanit',
                        fontSize: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {e.monogram}
                    </span>
                    <span
                      style={{
                        fontFamily: 'Bebas Neue, Kanit',
                        fontSize: 18,
                        letterSpacing: '.04em',
                        lineHeight: 1,
                      }}
                    >
                      {e.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <AmInput
            label="Key"
            value={values.key}
            onChange={(v) => onField('key', v)}
            mono
            valid={connected}
          />
          <AmInput
            label="Secret"
            type="password"
            value={values.secret}
            onChange={(v) => onField('secret', v)}
            mono
            valid={connected}
          />
          {ex.needsPassphrase && (
            <AmInput
              label="Passphrase"
              type="password"
              value={values.passphrase}
              onChange={(v) => onField('passphrase', v)}
              mono
              valid={connected}
            />
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 8,
            }}
          >
            <ExchangeStatusChip state={values.status} />
            <button
              onClick={canTest ? onTest : undefined}
              disabled={!canTest}
              style={{
                height: 40,
                padding: '0 18px',
                fontSize: 11,
                background: 'transparent',
                color: connected ? '#00B061' : canTest ? '#f08416' : '#787878',
                border: `1px solid ${
                  connected ? '#00B061' : canTest ? '#f08416' : '#383838'
                }`,
                fontFamily: 'Kanit',
                fontWeight: 700,
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                cursor: canTest ? 'pointer' : 'not-allowed',
              }}
            >
              Test Connection
            </button>
          </div>
          {error && (
            <div
              style={{
                fontFamily:
                  'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
                fontSize: 11,
                color: '#F0162F',
                letterSpacing: '.04em',
              }}
            >
              ! {error}
            </div>
          )}
        </div>
        <div
          style={{
            borderLeft: narrow ? 'none' : '1px solid #383838',
            paddingLeft: narrow ? 0 : 40,
            borderTop: narrow ? '1px solid #383838' : undefined,
            paddingTop: narrow ? 24 : undefined,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minHeight: 0,
          }}
        >
          <div
            onClick={narrow ? () => setInstructionsOpen((o) => !o) : undefined}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              cursor: narrow ? 'pointer' : 'default',
            }}
          >
            <MonoTag color="#f08416">
              {narrow
                ? `${instructionsOpen ? '▾' : '▸'} ${
                    ex.name
                  } setup instructions`
                : `// ${ex.name} setup instructions`}
            </MonoTag>
            <MonoTag color="#787878">
              {String(ex.steps.length).padStart(2, '0')} steps
            </MonoTag>
          </div>
          {(!narrow || instructionsOpen) && (
            <div
              style={{
                flex: narrow ? undefined : 1,
                overflow: narrow ? 'visible' : 'auto',
              }}
            >
              <ExchangeInstructions exchange={ex} accent="#f08416" dim />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExchangeStep;
